import { Button } from '@/components/ui/button';
import { BookOpen, Eye, EyeOff, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axiosInstance from '@/axios/axios';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { useAuth } from '@/hooks/auth/use-auth';

const passwordSchema = z
  .object({
    password1: z.string().min(5, 'Password must be at least 5 characters'),
    password2: z.string(),
  })
  .refine((data) => data.password1 === data.password2, {
    message: 'Passwords do not match',
    path: ['password2'],
  });

type PasswordFormValues = z.infer<typeof passwordSchema>;

export const StudentOnboarding = ({ username }: { username: string }) => {
  const [showPassword1, setShowPassword1] = useState(false);
  const [showPassword2, setShowPassword2] = useState(false);
  const { logout } = useAuth();

  const form = useForm<PasswordFormValues>({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      password1: '',
      password2: '',
    },
  });

  const onSubmit = async (data: PasswordFormValues) => {
    try {
      await axiosInstance.post('/update-password-onboarding', {
        username: username,
        password: data.password1,
      });
      toast.success(
        'Password updated successfully! You will now be redirected to the login screen. Please use your new password to log in.'
      );
      setTimeout(() => {
        logout(); // Logging out user to refresh token
      }, 2000);
    } catch (err) {
      console.error('Failed to update password:', err);
      toast.error('Failed to update password');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-center">
        <BookOpen className="h-16 w-16 text-primary" />
      </div>
      <div className="text-center space-y-2">
        <h3 className="text-lg font-semibold">Begin Your Learning Journey</h3>
        <p className="text-sm text-muted-foreground">
          Please update your password before accessing your courses and assignments.
        </p>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="password1"
            render={({ field }) => (
              <FormItem>
                <FormLabel>New Password</FormLabel>
                <div className="relative">
                  <FormControl>
                    <Input
                      type={showPassword1 ? 'text' : 'password'}
                      placeholder="Enter new password"
                      {...field}
                      className="pr-10"
                    />
                  </FormControl>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                    onClick={() => setShowPassword1(!showPassword1)}
                  >
                    {showPassword1 ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="password2"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Confirm Password</FormLabel>
                <div className="relative">
                  <FormControl>
                    <Input
                      type={showPassword2 ? 'text' : 'password'}
                      placeholder="Confirm new password"
                      {...field}
                      className="pr-10"
                    />
                  </FormControl>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                    onClick={() => setShowPassword2(!showPassword2)}
                  >
                    {showPassword2 ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button type="submit" className="w-full" disabled={form.formState.isSubmitting}>
            {form.formState.isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Updating Password...
              </>
            ) : (
              'Update Password'
            )}
          </Button>
        </form>
      </Form>
    </div>
  );
};
